import { useConnectionStore, formatTime } from '@sentinel/web-core';
import { StateBadge, type StateTone } from '@sentinel/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HEALTH_POLL_MS, HEALTH_URL } from './scene/scene-config';

type Health = 'checking' | 'ok' | 'off';

const TONE: Record<Health, StateTone> = { checking: 'unk', ok: 'ok', off: 'off' };

/** Liveness of the sign-in service, polled while the page is open; offline = unreachable. */
function useHealth(): Health {
  const online = useConnectionStore((s) => s.browserOnline);
  const [health, setHealth] = useState<Health>('checking');
  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        const res = await fetch(HEALTH_URL, { cache: 'no-store' });
        if (alive) setHealth(res.ok ? 'ok' : 'off');
      } catch {
        if (alive) setHealth('off');
      }
    };
    void probe();
    const timer = window.setInterval(() => void probe(), HEALTH_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [online]);
  return online ? health : 'off';
}

function useClock(): string {
  const [now, setNow] = useState(() => new Date().toISOString());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date().toISOString()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/** Service badge (colour + glyph + word) and the local clock, bottom of the scene. */
export function ServiceStatus() {
  const { t } = useTranslation();
  const health = useHealth();
  const now = useClock();
  return (
    <div className="acc-services">
      <span className="acc-chip">
        {t('accounts.services.accounts')}
        <StateBadge tone={TONE[health]} label={t(`accounts.services.${health}`)} />
      </span>
      <span className="acc-clock">{formatTime(now, true)}</span>
    </div>
  );
}
